import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { rowClick } from '../../utils/rowNav';
import {
    Device,
    deleteDevice,
    fetchDeviceDeletePreview,
    listDevices,
    reorderDevices,
    updateDevice,
    publishDevice,
} from '../../api/device';
import {
    PublishBadge,
    PublishToggle,
    isPublishedValue,
} from '../../components/PublishBadge';
import BatchActions, {
    HeaderCheckbox,
    RowCheckbox,
} from '../../components/BatchActions';
import { useBatchSelect } from '../../utils/useBatchSelect';
import { useCopyText } from '../../utils/useCopyText';
import DeleteCascadeModal from '../../components/DeleteCascadeModal';
import { listProcessArchs, ProcessArch } from '../../api/parameter';
import { ApiError, alertApprovalIfPending } from '../../api/request';
import { useCan } from '../../store';
import { useIsMaintainer } from '../../utils/readonlyScope';
import { useDictText } from '../../components/DictSelect';
import DeviceCreateModal from './DeviceCreateModal';
import MModeRefModal from './MModeRefModal';
import DeviceExtValueModal from './DeviceExtValueModal';
import BundleImportModal from '../../components/BundleImportModal';
import { alertDialog } from '../../utils/dialog';
import SearchSelect from '../../components/SearchSelect';

// 设备管理 tab (2026-07-31 需求): 平铺管理现有设备 + 手动排序 (sno 字段).
// 排序规则: sno 升序, 0=未设置排最后; ↑/↓/置顶 调整后整表依当前顺序重写 sno=1..N
// (一次 PUT /devices-reorder, 仅管理员). 新建/编辑复用 DeviceCreateModal.

function DeviceManage() {
    const canCreate = useCan('device', 'create');
    const canUpdate = useCan('device', 'update');
    const canDelete = useCan('device', 'delete');
    // 只读访问者只看在用设备 (停用设备是维护过程态)
    const maintainer = useIsMaintainer('device');
    const canEditDevice = useCan('device', 'update');
    // 没有任何写权 → 整列「操作」不渲染, 点行进工况 (2026-09-12)
    const canManage = canUpdate || canDelete;
    const history = useHistory();
    // 光源展示中文名 (字典 light_source), 列表不露 code
    const lightText = useDictText('light_source');
    const [list, setList] = useState<Device[]>([]);
    const [archs, setArchs] = useState<ProcessArch[]>([]);
    const [archFilter, setArchFilter] = useState('');
    const [keyword, setKeyword] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [bundleOpen, setBundleOpen] = useState(false);
    // ★设计A: 加工模式引用维护弹窗 (设备侧主维护口)
    const [mmodeDevice, setMModeDevice] = useState<Device | null>(null);
    // 参-04: 设备动态字段值编辑弹窗 (字段组来自 Schema 管理→模板→设备)
    const [extValDevice, setExtValDevice] = useState<Device | null>(null);
    const [editing, setEditing] = useState<Device | null>(null);

    useEffect(() => {
        listProcessArchs()
            .then(setArchs)
            .catch(() => setArchs([]));
    }, []);

    // 设备总量 ~70, 一次拉全量; 排序操作需要完整顺序上下文, 不分页
    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listDevices({ pageSize: 500 });
            setList(res.list || []);
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
            setList([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // 展示行: 筛选只影响可见性, 排序操作始终基于完整 list (避免局部重排打乱全局 sno)
    const shown = useMemo(() => {
        const kw = keyword.trim().toLowerCase();
        return list.filter(
            (d) =>
                (maintainer || d.status === 'enabled') &&
                (!archFilter || d.archCode === archFilter) &&
                (!kw ||
                    (d.name || '').toLowerCase().includes(kw) ||
                    d.deviceKey.toLowerCase().includes(kw) ||
                    (d.internalKey || '').toLowerCase().includes(kw) ||
                    d.family.toLowerCase().includes(kw)),
        );
    }, [list, archFilter, keyword, maintainer]);

    // 已发布设备的键集合: 批量发布/取消发布据此只提交状态需要变的项 (设备列表不分页,
    // 逐项 find 是 O(n²))
    const publishedKeys = useMemo(
        () =>
            new Set(
                list
                    .filter((d) => isPublishedValue(d.isPublished))
                    .map((d) => d.deviceKey),
            ),
        [list],
    );

    // device_key 点击复制 (与工具市场复制 toolKey 同一套交互)
    const { copied, copy } = useCopyText();

    // 批量操作 (2026-09-10): 设备列表不分页, 选中随筛选条件变化清空
    const sel = useBatchSelect(
        useMemo(() => shown.map((d) => d.deviceKey), [shown]),
        [archFilter, keyword].join('|'),
    );

    const filtered = archFilter !== '' || keyword.trim() !== '';

    // 把完整 list 的当前顺序落库 (sno=1..N)
    const persistOrder = async (next: Device[]) => {
        setList(next.map((d, i) => ({ ...d, sno: i + 1 }))); // 先本地生效 (含新 sno), 失败再回读
        setSaving(true);
        try {
            await reorderDevices(next.map((d) => d.deviceKey));
        } catch (e) {
            alertDialog(
                `排序保存失败: ${e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)}`,
            );
            await load();
        } finally {
            setSaving(false);
        }
    };

    const move = (deviceKey: string, dir: -1 | 0 | 1) => {
        const idx = list.findIndex((d) => d.deviceKey === deviceKey);
        if (idx < 0) return;
        const next = [...list];
        const [row] = next.splice(idx, 1);
        if (dir === 0) {
            next.unshift(row); // 置顶
        } else {
            const to = Math.min(Math.max(idx + dir, 0), next.length);
            next.splice(to, 0, row);
        }
        persistOrder(next);
    };

    const onToggleStatus = async (d: Device) => {
        const to = d.status === 'enabled' ? 'disabled' : 'enabled';
        try {
            const res = await updateDevice(d.deviceKey, { status: to });
            await load();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(
                `更新失败: ${e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)}`,
            );
        }
    };

    // 硬删语义 (2026-08-21): 删除前弹关联数据预检, 勾选级联项随工单进审批.
    const [delTarget, setDelTarget] = useState<Device | null>(null);
    const onDelete = (d: Device) => setDelTarget(d);

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-xs text-slate-500">
                        共 {list.length} 台 · 按 sno 手动排序 (↑/↓/置顶,
                        全站设备下拉与列表同序)
                        {saving && (
                            <span className="text-brand-600"> · 保存中…</span>
                        )}
                    </div>
                </div>
                <div className="flex gap-2">
                    {/* 批量操作放最前 (同材料列表): 与左侧勾选列在同一视线上 */}
                    <BatchActions
                        resource="device"
                        entityLabel="设备"
                        deleteKeyField="deviceKey"
                        publishable
                        publishedOf={(k) => publishedKeys.has(k)}
                        selected={sel.selectedKeys}
                        onClear={sel.clear}
                        onDone={load}
                    />
                    <button
                        type="button"
                        className="text-sm px-3 py-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                        onClick={() => setBundleOpen(true)}
                        title="一份 JSON/YAML 文件一次创建 设备+模式+能力+校准+参数"
                        hidden={!canUpdate}
                    >
                        ⬆ 套件导入
                    </button>
                    {/* 新建设备 = 创建向导 (2026-09-17): 设备 + 模式 + 字段组 + 标定 + 模板绑定
                        一次填完、一个工单、一个事务落库。旧弹窗仍保留给「编辑」用 ——
                        编辑是改已有设备, 没有"新建走审批时 ext 无处可写"的问题。 */}
                    <button
                        type="button"
                        className="text-sm px-3 py-1.5 rounded border border-brand-200 text-brand-600 hover:bg-brand-50"
                        title="分步创建: 基本信息 → 加工模式 → 字段组 → 功率标定 → 模板绑定 → 预检提交"
                        onClick={() => history.push('/devices/new')}
                        hidden={!canCreate}
                    >
                        + 新建设备
                    </button>
                </div>
            </div>

            <div className="card p-3">
                <div className="flex flex-wrap gap-3 items-end">
                    <div>
                        <label className="field-label">关键词</label>
                        <input
                            type="search"
                            className="inp w-56"
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            placeholder="搜 名称 / 编码"
                        />
                    </div>
                    <div>
                        <label className="field-label">工艺架构</label>
                        <SearchSelect
                            className="w-40"
                            value={archFilter}
                            onChange={setArchFilter}
                            placeholder="全部架构"
                            options={archs.map((a) => ({
                                value: a.code,
                                label: `${a.icon} ${a.label}`,
                            }))}
                            clearable
                        />
                    </div>
                    {filtered && (
                        <span className="text-[11px] text-amber-600 self-center">
                            筛选中仅隐藏行, 排序仍作用于全部设备
                        </span>
                    )}
                    <span className="ml-auto text-xs text-slate-400">
                        显示 {shown.length} 台
                    </span>
                </div>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            <div className="card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr>
                            <th className="th w-8" hidden={!canDelete}>
                                <HeaderCheckbox hidden={!canDelete} sel={sel} />
                            </th>
                            <th className="th w-14">sno</th>
                            <th className="th">设备名称</th>
                            <th className="th">设备标识 (device_key)</th>
                            <th className="th">内部编码</th>
                            <th className="th">工艺架构</th>
                            <th className="th">光源/动力</th>
                            <th className="th">功率(W)</th>
                            <th className="th">状态</th>
                            <th
                                className="th"
                                title="只约束 OpenAPI 对外下发 (挂在设备上的参数行还需引用材料同时已发布); 平台内不受限"
                            >
                                发布
                            </th>
                            <th className="th w-80" hidden={!canManage}>
                                操作
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td
                                    colSpan={11}
                                    className="td text-center text-slate-400 py-8"
                                >
                                    加载中…
                                </td>
                            </tr>
                        ) : shown.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={11}
                                    className="td text-center text-slate-400 py-8"
                                >
                                    暂无设备
                                </td>
                            </tr>
                        ) : (
                            shown.map((d, i) => (
                                <tr
                                    key={d.deviceKey}
                                    className="hover:bg-slate-50 cursor-pointer"
                                    title="点击查看工况"
                                    onClick={rowClick(() =>
                                        history.push(
                                            `/devices?deviceKey=${encodeURIComponent(d.deviceKey)}`,
                                        ),
                                    )}
                                >
                                    <td className="td" hidden={!canDelete}>
                                        <RowCheckbox
                                            hidden={!canDelete}
                                            sel={sel}
                                            k={d.deviceKey}
                                        />
                                    </td>
                                    <td className="td text-xs text-slate-400 font-mono">
                                        {d.sno || '—'}
                                    </td>
                                    {/* 点设备名进「设备树/工况」看该台的完整工况 (走 DeviceTopSelector 的 ?deviceKey 深链) */}
                                    <td className="td text-xs font-semibold">
                                        <a
                                            className="text-brand-600 cursor-pointer hover:underline"
                                            title={`查看工况 · device_key: ${d.deviceKey}`}
                                            onClick={() =>
                                                history.push(
                                                    `/devices?deviceKey=${encodeURIComponent(d.deviceKey)}`,
                                                )
                                            }
                                        >
                                            {d.name || d.deviceKey}
                                        </a>
                                    </td>
                                    {/* device_key: 参数导入模板 / YAML / 下游接口都按它认设备, 点一下直接复制 (不触发行跳转) */}
                                    <td className="td font-mono text-xs text-slate-600">
                                        <button
                                            type="button"
                                            className="inline-flex items-center gap-1 hover:text-brand-600"
                                            title="点击复制 device_key (参数导入模板的 deviceKey 列填的就是它)"
                                            onClick={(e) => copy(e, d.deviceKey)}
                                        >
                                            {d.deviceKey}
                                            <span className="text-slate-400">
                                                {copied === d.deviceKey
                                                    ? '✓'
                                                    : '📋'}
                                            </span>
                                        </button>
                                    </td>
                                    <td className="td font-mono text-xs">
                                        {d.internalKey || '—'}
                                    </td>
                                    <td className="td text-xs">{d.archCode}</td>
                                    {/* 光源/功率来自 capability ext 组 (列表接口随行附带); 光源是 light_source 字典 code, 展示中文名 */}
                                    <td
                                        className="td text-xs"
                                        title={d.capability?.source}
                                    >
                                        {lightText(d.capability?.source)}
                                    </td>
                                    <td className="td text-xs">
                                        {d.capability?.stdW || '—'}
                                    </td>
                                    <td className="td">
                                        <span
                                            className={`tag ${
                                                d.status === 'enabled'
                                                    ? 'bg-emerald-50 text-emerald-700'
                                                    : 'bg-slate-100 text-slate-500'
                                            }`}
                                        >
                                            {d.status === 'enabled'
                                                ? '启用'
                                                : '停用'}
                                        </span>
                                    </td>
                                    <td className="td">
                                        <PublishBadge
                                            isPublished={d.isPublished}
                                            publishedAt={d.publishedAt}
                                            layout="inline"
                                        />
                                    </td>
                                    <td className="td" hidden={!canManage}>
                                        <div className="flex items-center gap-1.5 text-xs whitespace-nowrap">
                                            {canEditDevice && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn-ghost px-1.5 py-0.5 disabled:opacity-30"
                                                        title="上移"
                                                        disabled={
                                                            saving ||
                                                            filtered ||
                                                            i === 0
                                                        }
                                                        onClick={() =>
                                                            move(
                                                                d.deviceKey,
                                                                -1,
                                                            )
                                                        }
                                                        hidden={!canUpdate}
                                                    >
                                                        ↑
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-ghost px-1.5 py-0.5 disabled:opacity-30"
                                                        title="下移"
                                                        disabled={
                                                            saving ||
                                                            filtered ||
                                                            i ===
                                                                shown.length - 1
                                                        }
                                                        onClick={() =>
                                                            move(d.deviceKey, 1)
                                                        }
                                                        hidden={!canUpdate}
                                                    >
                                                        ↓
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-ghost px-1.5 py-0.5 disabled:opacity-30"
                                                        title="置顶"
                                                        disabled={
                                                            saving ||
                                                            filtered ||
                                                            i === 0
                                                        }
                                                        onClick={() =>
                                                            move(d.deviceKey, 0)
                                                        }
                                                        hidden={!canUpdate}
                                                    >
                                                        ⤒
                                                    </button>
                                                </>
                                            )}
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                onClick={() => {
                                                    setEditing(d);
                                                    setModalOpen(true);
                                                }}
                                                hidden={!canUpdate}
                                            >
                                                编辑
                                            </button>
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                title="维护该设备支持的加工模式 (★设计A; 与模式能力是不同维度)"
                                                onClick={() =>
                                                    setMModeDevice(d)
                                                }
                                                hidden={!canUpdate}
                                            >
                                                加工模式
                                            </button>
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                title="填写该设备的动态字段值 (字段组在 Schema 管理→模板→设备 定义)"
                                                onClick={() =>
                                                    setExtValDevice(d)
                                                }
                                                hidden={!canUpdate}
                                            >
                                                字段
                                            </button>
                                            <button
                                                type="button"
                                                className="text-slate-500 hover:underline"
                                                onClick={() =>
                                                    onToggleStatus(d)
                                                }
                                                hidden={!canUpdate}
                                            >
                                                {d.status === 'enabled'
                                                    ? '停用'
                                                    : '启用'}
                                            </button>
                                            <PublishToggle
                                                resource="device"
                                                published={isPublishedValue(
                                                    d.isPublished,
                                                )}
                                                label={d.name || d.deviceKey}
                                                onToggle={(p) =>
                                                    publishDevice(
                                                        d.deviceKey,
                                                        p,
                                                    )
                                                }
                                                onDone={load}
                                            />
                                            <button
                                                type="button"
                                                className="text-rose-600 hover:underline"
                                                onClick={() => onDelete(d)}
                                                hidden={!canDelete}
                                            >
                                                删除
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {mmodeDevice && (
                <MModeRefModal
                    device={mmodeDevice}
                    onClose={() => setMModeDevice(null)}
                />
            )}
            {extValDevice && (
                <DeviceExtValueModal
                    device={extValDevice}
                    onClose={() => setExtValDevice(null)}
                />
            )}

            <DeviceCreateModal
                open={modalOpen}
                editing={editing}
                onClose={() => setModalOpen(false)}
                onSaved={() => {
                    setModalOpen(false);
                    load();
                }}
            />

            <DeleteCascadeModal
                open={!!delTarget}
                title="删除设备"
                targetLabel={
                    delTarget
                        ? `${delTarget.deviceKey} ${delTarget.name || ''}`
                        : ''
                }
                loadPreview={() =>
                    delTarget
                        ? fetchDeviceDeletePreview(delTarget.deviceKey)
                        : Promise.resolve([])
                }
                onClose={() => setDelTarget(null)}
                onConfirm={async (payload) => {
                    if (!delTarget) return;
                    const res = await deleteDevice(
                        delTarget.deviceKey,
                        payload,
                    );
                    await load();
                    alertApprovalIfPending(res.message);
                }}
            />

            <BundleImportModal
                kind="device"
                open={bundleOpen}
                onClose={() => setBundleOpen(false)}
                onImported={load}
            />
        </div>
    );
}

export default DeviceManage;
